const CONDITIONAL_MATCH_TITLE = "Conditional Match";
const LOGIC_TITLE = "Logic";
const SELECT_CONDITIONS = "Select conditions";

const CONDITION_TYPE_LABELS: Record<string, string> = {
    contactAttribute: "Contact Attribute",
    customEvent: "Custom Event",
    custom: "Custom Event",
    regalVoiceEvent: "Regal Voice Event",
    triggeringEvent: "Triggering Event",
};

const OPERATOR_LABELS: Record<string, string> = {
    after: "Is After",
    before: "Is Before",
    contains: "Contains",
    endsWith: "Ends With",
    equals: "Equals",
    greaterThan: "Is Greater Than",
    greaterThanEquals: "Is Greater Than or Equal To",
    isNotNull: "Is Set",
    isNotSet: "Does Not Exist",
    isOneOf: "Is One Of",
    isSet: "Exists",
    lessThan: "Is Less Than",
    lessThanEquals: "Is Less Than or Equal To",
    notEquals: "Does Not Equal",
    onOrAfter: "Is On or After",
    onOrBefore: "Is On or Before",
    startsWith: "Starts With",
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return undefined;
    }
    return value as Record<string, unknown>;
}

function isV1ConditionalPopulated(data: Record<string, unknown>): boolean {
    return Boolean(
        data.eventName || data.contactAttribute || (data.conditionalType === "triggeringEvent" && data.propertyName)
    );
}

/** Filter count shown on the Conditional canvas block; undefined when the Filters row is hidden. */
export function getConditionalFiltersCount(data: Record<string, unknown> | undefined): number | undefined {
    if (!data) {
        return undefined;
    }

    const filtersLength = Array.isArray(data.filters) ? data.filters.length : 0;
    if (isV1ConditionalPopulated(data) && filtersLength === 0) {
        return 1;
    }

    return filtersLength > 0 ? filtersLength : undefined;
}

export function isConditionalNodeConfigured(data: Record<string, unknown> | undefined): boolean {
    if (!data) {
        return false;
    }
    return Boolean(getConditionalFiltersCount(data) || data.empty);
}

/** Canvas block type title: "Conditional Match" when named, otherwise "Logic". */
export function getConditionalNodeTitle(data: Record<string, unknown> | undefined): string {
    const nodeName = typeof data?.nodeName === "string" ? data.nodeName : undefined;
    return isConditionalNodeConfigured(data) && nodeName ? CONDITIONAL_MATCH_TITLE : LOGIC_TITLE;
}

/** Canvas body text: custom name, "Conditional Match", or "Select conditions". */
export function getConditionalNodeBody(data: Record<string, unknown> | undefined): string {
    if (!isConditionalNodeConfigured(data)) {
        return SELECT_CONDITIONS;
    }
    return typeof data?.nodeName === "string" && data.nodeName ? data.nodeName : CONDITIONAL_MATCH_TITLE;
}

export function formatConditionalTypeLabel(conditionalType: unknown): string | undefined {
    if (typeof conditionalType !== "string" || !conditionalType) {
        return undefined;
    }
    return CONDITION_TYPE_LABELS[conditionalType] ?? conditionalType;
}

export function formatConditionalOperatorLabel(operator: unknown): string | undefined {
    if (typeof operator !== "string" || !operator) {
        return undefined;
    }
    return OPERATOR_LABELS[operator] ?? operator;
}

function formatFilterValue(filter: Record<string, unknown>): string | undefined {
    const propertyValue = filter.propertyValue;
    if (Array.isArray(propertyValue)) {
        const joined = propertyValue.map(String).filter(Boolean).join(", ");
        return joined || undefined;
    }
    if (typeof propertyValue === "string" && propertyValue) {
        return propertyValue;
    }
    if (typeof propertyValue === "number" || typeof propertyValue === "boolean") {
        return String(propertyValue);
    }
    if (typeof filter.relativeDateValue === "string" && filter.relativeDateValue) {
        return filter.relativeDateValue;
    }
    return undefined;
}

function formatFilterCriterion(filter: Record<string, unknown>): string | undefined {
    const propertyName =
        (typeof filter.propertyName === "string" && filter.propertyName) ||
        (typeof filter.contactAttribute === "string" && filter.contactAttribute) ||
        undefined;
    const operator = formatConditionalOperatorLabel(filter.propertyOperator);
    const value = formatFilterValue(filter);
    const dateComparisonType =
        typeof filter.dateComparisonType === "string" &&
        filter.dateComparisonType &&
        filter.dateComparisonType !== "specific"
            ? filter.dateComparisonType
            : undefined;

    const parts = [propertyName, operator, value, dateComparisonType].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : undefined;
}

function getLegacyConditionalFilter(data: Record<string, unknown>): Record<string, unknown> {
    return {
        propertyName: data.contactAttribute ?? data.propertyName,
        propertyOperator: data.propertyOperator,
        propertyValue: data.propertyValue,
        relativeDateValue: data.relativeDateValue,
        dateComparisonType: data.dateComparisonType,
    };
}

/** Human-readable filter lines matching Journey Edit property criteria. */
export function getConditionalFilterSummaries(data: Record<string, unknown> | undefined): string[] {
    if (!data) {
        return [];
    }

    if (Array.isArray(data.filters) && data.filters.length > 0) {
        return data.filters
            .map((filter) => formatFilterCriterion(asRecord(filter) ?? {}))
            .filter((summary): summary is string => Boolean(summary));
    }

    if (isV1ConditionalPopulated(data)) {
        const summary = formatFilterCriterion(getLegacyConditionalFilter(data));
        return summary ? [summary] : [];
    }

    return [];
}
